import { daysBetween, parseISOToLocalDate } from './dateUtils';

interface EpisodeWindow {
  id: string;
  start_date: string;
  end_date: string;
}

interface EpisodeScopedFall {
  fall_date: string;
  episode_id?: string | null;
}

// Ties go to the episode that starts later, then to the later id, so the
// result is the same whatever order the episodes arrive in.
const outranks = (candidate: EpisodeWindow, current: EpisodeWindow): boolean =>
  candidate.start_date !== current.start_date
    ? candidate.start_date > current.start_date
    : candidate.id > current.id;

/**
 * The one episode a fall is listed under, or null when the patient has no
 * episodes.
 *
 * A fall linked to a known episode belongs to it. A fall with no usable link
 * (imported history, or its episode was removed) goes to the episode whose
 * dates contain the fall date, and failing that to the episode closest in
 * date, so every fall is listed under exactly one episode.
 */
export const assignFallToEpisode = (
  fall: EpisodeScopedFall,
  episodes: readonly EpisodeWindow[]
): string | null => {
  if (episodes.length === 0) return null;
  if (fall.episode_id && episodes.some(e => e.id === fall.episode_id)) {
    return fall.episode_id;
  }

  const fallDate = parseISOToLocalDate(fall.fall_date);
  let best: EpisodeWindow | undefined;
  let bestDistance = Infinity;
  if (fallDate) {
    for (const episode of episodes) {
      const start = parseISOToLocalDate(episode.start_date);
      const end = parseISOToLocalDate(episode.end_date);
      if (!start || !end) continue;
      // Whole calendar days outside the window. Zero inside it, both ends
      // inclusive.
      const distance = Math.max(
        daysBetween(fallDate, start),
        daysBetween(end, fallDate),
        0
      );
      if (
        !best ||
        distance < bestDistance ||
        (distance === bestDistance && outranks(episode, best))
      ) {
        best = episode;
        bestDistance = distance;
      }
    }
  }

  // Nothing could be compared by date, so the tie-break order alone picks.
  // The fall still has to be listed somewhere.
  if (!best) {
    best = episodes.reduce((current, episode) =>
      outranks(episode, current) ? episode : current
    );
  }
  return best.id;
};

/**
 * The falls listed under the selected episode. With no selected episode, or no
 * episodes to assign against, every fall is returned.
 */
export const filterFallsForEpisode = <T extends EpisodeScopedFall>(
  falls: readonly T[],
  episodes: readonly EpisodeWindow[],
  selectedEpisodeId: string | null | undefined
): T[] => {
  if (!selectedEpisodeId || episodes.length === 0) return [...falls];
  return falls.filter(
    fall =>
      fall.episode_id === selectedEpisodeId ||
      assignFallToEpisode(fall, episodes) === selectedEpisodeId
  );
};
